import React, { useState, useImperativeHandle, useRef, forwardRef, useEffect } from 'react';
import { FormGroup, Input, Select, Button, message, DatePicker, OverlayTrigger, PopOver, Icon } from '@tencent/spaui';
import '../regist/formComponent/info-common.scss';
import { post } from 'store/request';
import dayjs from 'dayjs';
import { useAppDispatch, useTypedSelector } from 'store/index';
import { getCsIndustryTree } from 'store/manager';
import './form.scss';
import {
  TARGET_TYPE,
  TARGET_TYPE_KV,
  DEMAND_TYPE,
  DEMAND_TYPE_KV,
  TENDER_NUM,
  TENDER_NUM_KV,
} from 'pages/manage/orderSquare/constants/index.js';
import CitySelect from 'pages/regist/components/CitySelect';

const numReg = /^[0-9]*$/;

const Tips = (props: { content: string }) => (
  <OverlayTrigger
    placement="top"
    overlay={<PopOver>您选择的参与竞标服务商，可通过此信息与您取得联系。请仔细填写。</PopOver>}
  >
    <div>
      {props.content}
      <Icon
        style={{
          cursor: 'pointer',
          verticalAlign: 'text-bottom',
          width: '14px',
          marginLeft: '4px',
        }}
        name="info-filled"
      />
    </div>
  </OverlayTrigger>
);

const baseState = {
  target: '', // 需求目标
  target_other: '', // (其他)需求目标描述
  type: '', // 需求类型
  budget: '', // 预算金额
  content: '', // 需求详情
  tender_expect_deadline: '', // 服务商投标截止时间
  tender_expect_num: '', // 参与投标服务商数量
  company_name: '', // 公司名称
  company_brand: '', // 产品/品牌线
  company_industry: '', // 所属行业
  company_location: '', // 公司所在地
  contact_name: '', // 用户名称
  contact_telephone: '', // 手机号码
  code: '', // 验证码
  contact_email: '', // 邮箱
  contact_address: '', // 联系人地址
};

const FormExamStrateg: { [key: string]: any } = {
  target: (value: any) => {
    if (value === '') {
      return '请选择需求目标';
    }
    return false;
  },
  target_other: (value: any, formData: any) => {
    if (value === '' && formData.target === 8) {
      return '请输入需求描述';
    }
    if (value.length > 400) {
      return '内容过长';
    }
    return false;
  },
  type: (value: any) => {
    if (value === '') {
      return '请选择需求类型';
    }
    return false;
  },
  // 预算金额
  budget: (value: any) => {
    if (!value) {
      return '请输入预算金额';
    }
    if (value.length > 20) {
      return '内容过长';
    }
    return false;
  },
  // 需求详情
  content: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length > 400) {
      return '内容过长';
    }
    return false;
  },
  // 服务商投标截止时间
  tender_expect_deadline: (value: any) => {
    if (!value) {
      return '请选择服务商投标截止时间';
    }
    return false;
  },
  tender_expect_num: (value: any) => {
    if (value === '') {
      return '请选择参与投标服务商数量';
    }
    return false;
  },

  // 公司名称
  company_name: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length > 50) {
      return '内容过长';
    }
    return false;
  },
  company_brand: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length > 50) {
      return '内容过长';
    }
    return false;
  },
  company_industry: (value: any) => {
    if (value === '') {
      return '请选择行业';
    }
    return false;
  },
  // 公司所在地
  company_location: (value: any) => {
    if (!value) {
      return '请选择公司所在地';
    }
    return false;
  },
  // 用户名称
  contact_name: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length < 2) {
      return '内容过短';
    }
    if (value.length > 10) {
      return '内容过长';
    }
    return false;
  },
  contact_telephone: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length > 11) {
      return '内容过长';
    }
    if (isNaN(+value)) {
      return '请输入正确手机号码';
    }
    return false;
  },
  code: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length !== 6) {
      return '请输入正确校验码';
    }
    if (isNaN(+value)) {
      return '请输入正确校验码';
    }
    return false;
  },
  contact_email: (value: any) => {
    if (!value) {
      return '不能为空';
    }
    if (value.length > 50) {
      return '内容过长';
    }
    if (!value.includes('@')) {
      return '请输入正确的邮箱';
    }
    return false;
  },
  contact_address: (value: any) => {
    if (value.length > 400) {
      return '内容过长';
    }
    return false;
  },
};

const Feedback = (num: number, total: number) => (
  <span>
    <span className="spaui-input-feedback-count">{num}</span>
    <span className="spaui-input-feedback-divider">/</span>
    <span className="spaui-input-feedback-range">{total}</span>
  </span>
);

const lineHeight36 = { lineHeight: '36px' };

interface Props {
  children?: React.FunctionComponent<{ submit: () => void }>;
  onSubmit?: (data: any) => void;
  initData?: { [key: string]: any };
  disabled: boolean;
  isCustomerView: boolean;
}

// 基本信息
// eslint-disable-next-line react/display-name
const BaseInfo: React.FC<Props> = forwardRef((props, cref) => {
  const { disabled } = props;
  const { isCustomerView } = props;
  // 表单所有数据
  const [formData, setFormData] = useState<{ [key: string]: any }>({ ...baseState });
  // 表单项检查
  const [formExam, setFormExam] = useState<{ [key: string]: any }>({});

  const [verifyCodeTime, setTime] = useState(0);

  const {
    manager: { csIndustryTree },
  } = useTypedSelector((state) => state);

  const setFormValue = (name: string, value: any) => {
    formData[name] = value;
    setFormData({ ...formData });
  };

  // 返回 true 代表表单正确
  const checkForm = (key: string): boolean => {
    const res = FormExamStrateg[key]?.(formData[key], formData);
    formExam[key] = res;
    setFormExam({ ...formExam });
    return !res;
  };

  const countDown = (time: number) => {
    setTime(time);
    if (time === 0) {
      return;
    }
    setTimeout(countDown, 1000, time - 1);
  };

  const getCode = () => {
    if (checkForm('contact_telephone') && (window as any).TencentCaptcha) {
      try {
        // 直接生成一个验证码对象
        const captcha1 = new (window as any).TencentCaptcha(
          '2075023792',
          (res: { ret: number; ticket: string; randstr: string }) => {
            if (res.ret !== 0) {
              return;
            }
            post('/tof/sms', {
              phone: `${formData.contact_telephone}`,
              ticket: res.ticket,
              randstr: res.randstr,
            }).then(() => {
              message.success('验证码发送成功');
              countDown(60);
            });
          }
        );
        captcha1.show(); // 显示验证码
      } catch (error) {
        console.log(error);
      }
    }
  };

  const submit = () => {
    const result = Object.keys(FormExamStrateg).map((item) => checkForm(item));
    if (result.includes(false)) {
      return false;
    }
    props.onSubmit?.(formData);
    return formData;
  };

  const Component = props.children;

  const contentRef = useRef();
  useImperativeHandle(cref, () => ({
    submit,
  }));

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getCsIndustryTree());
    /* eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, []);

  useEffect(() => {
    if (props.initData) {
      setFormData(props.initData);
    } else {
      setFormData({ ...baseState });
    }
  }, [props.initData]);

  if (disabled) {
    return (
      <div ref={contentRef}>
        <div className="info-content">
          <div className="form-line">
            <span className="form-label">需求目标</span>
            <div className="form-item " style={lineHeight36}>
              {TARGET_TYPE_KV[formData.target]}
            </div>
          </div>

          {formData.target === 8 && formData.target_other && (
            <div className="form-line">
              <span className="form-label" />
              <div className="form-item " style={lineHeight36}>
                {formData.target_other}
              </div>
            </div>
          )}

          <div className="form-line">
            <span className="form-label">需求类型</span>
            <div className="form-item " style={lineHeight36}>
              {DEMAND_TYPE_KV[formData.type]}
            </div>
          </div>

          <div className="form-line">
            <span className="form-label">预算金额</span>
            <div className="form-item " style={lineHeight36}>
              {formData.budget}
            </div>
          </div>

          <div className="form-line">
            <span className="form-label">需求详情</span>
            <FormGroup className="form-item" error={formExam.content} feedback={Feedback(formData.content.length, 400)}>
              <Input
                disabled={disabled}
                style={{ height: '300px', background: 'white', cursor: 'auto' }}
                placeholder={
                  '请输入需求背景、需求目标、其他需求等内容，200个字以内\n举例：\n*需求背景：新说品牌，首次尝试视频号传播，希望可以找到食品/奶制品相关行业经验的服务商。\n*需求目标：希望进行视频号搭建和运营，年底增粉10万，直播5场\n' +
                  '等。\n*其他需求：希望服务商可以提供历史服务客户案例资料。'
                }
                value={formData.content}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('content', value);
                  checkForm('content');
                }}
                tagType="textarea"
              />
            </FormGroup>
          </div>

          <div className="form-line">
            <span className="form-label">服务商投标截止时间</span>
            <div className="form-item " style={lineHeight36}>
              {dayjs(formData.tender_expect_deadline).format('YYYY-MM-DD')}
            </div>
          </div>

          <div className="form-line">
            <span className="form-label">参与投标服务商数量</span>
            <div className="form-item " style={lineHeight36}>
              {TENDER_NUM_KV[formData.tender_expect_num]}
            </div>
          </div>

          <hr />

          {!isCustomerView && [
            <div className="form-line" key={'company_name'}>
              <span className="form-label">公司名称</span>
              <div className="form-item " style={lineHeight36}>
                {formData.company_name}
              </div>
            </div>,
            <div className="form-line" key={'company_brand'}>
              <span className="form-label">产品/品牌线</span>
              <div className="form-item " style={lineHeight36}>
                {formData.company_brand}
              </div>
            </div>,
          ]}

          <div className="form-line">
            <span className="form-label">所属行业</span>
            <FormGroup className="form-item industry—custom" error={formExam.company_industry}>
              <Select
                disabled={disabled}
                value={formData.company_industry}
                data={csIndustryTree}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('company_industry', +value);
                  checkForm('company_industry');
                }}
              />
            </FormGroup>
          </div>

          <div className="form-line">
            <span className="form-label">公司所在地</span>
            <FormGroup className="form-item location—custom" error={formExam.company_location}>
              <CitySelect
                disabled={disabled}
                value={formData.company_location}
                onChange={(value = '') => {
                  setFormValue('company_location', value);
                  checkForm('company_location');
                }}
              />
            </FormGroup>
          </div>

          {!isCustomerView && [
            <div className="form-line" key={'contact_name'}>
              <span className="form-label">联系人姓名</span>
              <div className="form-item " style={lineHeight36}>
                {formData.contact_name}
              </div>
            </div>,
            <div className="form-line" style={{ marginBottom: '22px' }} key={'contact_telephone'}>
              <span className="form-label">联系人手机号</span>
              <div className="form-item " style={lineHeight36}>
                {formData.contact_telephone}
              </div>
            </div>,
            <div className="form-line" key={'contact_email'}>
              <span className="form-label">联系人邮箱</span>
              <div className="form-item " style={lineHeight36}>
                {formData.contact_email}
              </div>
            </div>,
            <div className="form-line" key={'contact_address'}>
              <span className="form-label">联系人地址</span>
              <div className="form-item " style={lineHeight36}>
                {formData.contact_address}
              </div>
            </div>,
          ]}
        </div>
        <div style={{ display: 'inline-block' }}>{Component && <Component submit={submit} />}</div>
      </div>
    );
  }

  return (
    <div ref={contentRef}>
      <div className="info-content">
        <div className="form-line">
          <span className="form-label">需求目标</span>
          <FormGroup className="form-item" error={formExam.target}>
            <Select
              disabled={disabled}
              value={formData.target}
              data={TARGET_TYPE}
              onChange={(event: EventTarget, value = '') => {
                setFormValue('target', +value);
                checkForm('target');
              }}
            />
          </FormGroup>
        </div>

        {formData.target === 8 && ( // 其他 请描述
          <div className="form-line">
            <span className="form-label" />
            <FormGroup className="form-item" error={formExam.target_other}>
              <Input
                disabled={disabled}
                placeholder="请描述"
                value={formData.target_other}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('target_other', value.trim());
                  checkForm('target_other');
                }}
                underlined
              />
            </FormGroup>
          </div>
        )}

        <div className="form-line">
          <span className="form-label">需求类型</span>
          <FormGroup className="form-item" error={formExam.type}>
            <Select
              disabled={disabled}
              value={formData.type}
              data={DEMAND_TYPE}
              onChange={(event: EventTarget, value = '') => {
                setFormValue('type', +value);
                checkForm('type');
              }}
            />
          </FormGroup>
        </div>

        <div className="form-line">
          <span className="form-label">预算金额</span>
          <FormGroup className="form-item" error={formExam.budget} feedback={'元'}>
            <Input
              disabled={disabled}
              placeholder="请输入预算金额"
              value={formData.budget}
              onChange={(event: EventTarget, value = '') => {
                if (numReg.test(value.trim())) {
                  setFormValue('budget', value.trim());
                  checkForm('budget');
                }
              }}
              underlined
            />
          </FormGroup>
        </div>

        <div className="form-line">
          <span className="form-label">需求详情</span>
          <FormGroup className="form-item" error={formExam.content} feedback={Feedback(formData.content.length, 400)}>
            <Input
              disabled={disabled}
              style={{ height: '148px' }}
              placeholder={
                '请输入需求背景、需求目标、其他需求等内容，200个字以内\n举例：\n*需求背景：新说品牌，首次尝试视频号传播，希望可以找到食品/奶制品相关行业经验的服务商。\n*需求目标：希望进行视频号搭建和运营，年底增粉10万，直播5场\n' +
                '等。\n*其他需求：希望服务商可以提供历史服务客户案例资料。'
              }
              value={formData.content}
              onChange={(event: EventTarget, value = '') => {
                setFormValue('content', value);
                checkForm('content');
              }}
              tagType="textarea"
            />
          </FormGroup>
        </div>

        <div className="form-line">
          <span className="form-label">服务商投标截止时间</span>
          <FormGroup className="form-item" error={formExam.tender_expect_deadline}>
            <div style={{ display: 'flex' }}>
              <DatePicker
                disabled={disabled}
                date={formData.tender_expect_deadline}
                type="single"
                picker="date"
                optionalStartDate={dayjs().add(1, 'day').format('YYYY-MM-DD')}
                optionalEndDate={'2200-01-01'}
                onChange={(event: EventTarget, value) => {
                  setFormValue('tender_expect_deadline', dayjs(value).format('YYYY-MM-DD HH:mm:ss'));
                  checkForm('tender_expect_deadline');
                }}
              />
            </div>
          </FormGroup>
        </div>

        <div className="form-line">
          <span className="form-label">参与投标服务商数量</span>
          <FormGroup className="form-item" error={formExam.tender_expect_num}>
            <Select
              disabled={disabled}
              value={formData.tender_expect_num}
              data={TENDER_NUM}
              onChange={(event: EventTarget, value = '') => {
                setFormValue('tender_expect_num', +value);
                checkForm('tender_expect_num');
              }}
            />
          </FormGroup>
        </div>

        <hr />

        {!isCustomerView && [
          <div className="form-line" key={'company_name'}>
            <span className="form-label">公司名称</span>
            <FormGroup
              className="form-item"
              error={formExam.company_name}
              // feedback={Feedback(formData.company_name.length, 20)}
            >
              <Input
                disabled={disabled}
                placeholder="请输入公司名称"
                value={formData.company_name}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('company_name', value.trim());
                  checkForm('company_name');
                }}
                underlined
              />
            </FormGroup>
          </div>,
          <div className="form-line" key={'company_brand'}>
            <span className="form-label">产品/品牌线</span>
            <FormGroup
              className="form-item"
              error={formExam.company_brand}
              // feedback={Feedback(formData.company_brand.length, 20)}
            >
              <Input
                disabled={disabled}
                placeholder="请输入产品/品牌线"
                value={formData.company_brand}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('company_brand', value.trim());
                  checkForm('company_brand');
                }}
                underlined
              />
            </FormGroup>
          </div>,
        ]}

        <div className="form-line">
          <span className="form-label">所属行业</span>
          <FormGroup className="form-item" error={formExam.company_industry}>
            <Select
              disabled={disabled}
              value={formData.company_industry}
              data={csIndustryTree}
              onChange={(event: EventTarget, value = '') => {
                setFormValue('company_industry', +value);
                checkForm('company_industry');
              }}
            />
          </FormGroup>
        </div>

        <div className="form-line">
          <span className="form-label">公司所在地</span>
          <FormGroup className="form-item" error={formExam.company_location}>
            <CitySelect
              disabled={disabled}
              value={formData.company_location}
              onChange={(value = '') => {
                setFormValue('company_location', value);
                checkForm('company_location');
              }}
            />
          </FormGroup>
        </div>

        {!isCustomerView && (
          <div className="form-line">
            <span className="form-label">
              <Tips content="联系人姓名" />
            </span>
            <FormGroup
              className="form-item"
              error={formExam.contact_name}
              feedback={Feedback(formData.contact_name?.length, 10)}
            >
              <Input
                disabled={disabled}
                placeholder="请输入联系人姓名"
                value={formData.contact_name}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('contact_name', value.trim());
                  checkForm('contact_name');
                }}
                underlined
              />
            </FormGroup>
          </div>
        )}

        {!isCustomerView && [
          <div className="form-line" style={{ marginBottom: '22px' }} key={'contact_telephone'}>
            <span className="form-label">
              <Tips content="联系人手机号" />
            </span>
            <div className="form-item" style={{ display: 'flex' }}>
              <FormGroup style={{ flex: 1 }} error={formExam.contact_telephone}>
                <Input
                  disabled={disabled}
                  placeholder="请输入联系人手机号码"
                  value={formData.contact_telephone}
                  onChange={(event: EventTarget, value = '') => {
                    setFormValue('contact_telephone', value.trim());
                    checkForm('contact_telephone');
                  }}
                  underlined
                />
              </FormGroup>
              {verifyCodeTime === 0 ? (
                <Button disabled={disabled} onClick={getCode}>
                  获取校验码
                </Button>
              ) : (
                <Button disabled>重新获取({verifyCodeTime})</Button>
              )}
            </div>
          </div>,
          <div className="form-line" key={'code'}>
            <span className="form-label" />
            <FormGroup className="form-item" error={formExam.code}>
              <Input
                disabled={disabled}
                placeholder="填写校验码"
                value={formData.code}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('code', value.trim());
                  checkForm('code');
                }}
                underlined
              />
            </FormGroup>
          </div>,
          <div className="form-line" key={'contact_email'}>
            <span className="form-label">
              <Tips content="联系人邮箱" />
            </span>
            <FormGroup
              className="form-item"
              error={formExam.contact_email}
              // feedback={Feedback(formData['contact_email'].length, 50)}
            >
              <Input
                disabled={disabled}
                placeholder="请输入联系人的邮箱"
                value={formData.contact_email}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('contact_email', value.trim());
                  checkForm('contact_email');
                }}
                underlined
              />
            </FormGroup>
          </div>,
          <div className="form-line" key={'contact_address'}>
            <span className="form-label">联系人地址</span>
            <FormGroup
              className="form-item"
              error={formExam.contact_address}
              // feedback={Feedback(formData.contact_address.length, 20)}
            >
              <Input
                disabled={disabled}
                placeholder="联系人地址"
                value={formData.contact_address}
                onChange={(event: EventTarget, value = '') => {
                  setFormValue('contact_address', value.trim());
                  checkForm('contact_address');
                }}
                underlined
              />
            </FormGroup>
          </div>,
        ]}
      </div>
      <div style={{ display: 'inline-block' }}>{Component && <Component submit={submit} />}</div>
    </div>
  );
});

export default BaseInfo;
